import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Camera,
  ChevronDown,
  CircleUserRound,
  ExternalLink,
  KeyRound,
  Link2,
  Mail,
  MonitorCog,
  Palette,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import Alert from './Alert';
import { eraseCookie, getCookie } from '../utils/cookies';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import {
  getHoverPreviewPreference,
  HOVER_PREVIEW_GROUPS,
  resetHoverPreviewPreference,
  setHoverPreviewPreference,
} from '../utils/previewPreferences';
import { Toggle } from './FieldComponents';
import '../styles/Settings.css';
import '../Alert.css';
import { useNavVariant } from '../App';
import { useDocumentGenerationPreferences } from './DocumentGenerationPreferences';
import OutlookIntegrationPanel from './OutlookIntegrationPanel';

const API_ROOT = 'https://api.casedb.co';

const EMPTY_SHARED_LINKS_PAGINATION = {
  current_page: 1,
  per_page: 100,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

const EMPTY_OUTLOOK_MAILBOX_STATUS = {
  state: 'never',
  available: false,
  last_checked_at: null,
  last_success_at: null,
  safe_error: '',
};

const CASE_NOTIFICATION_SCOPES = [
  { value: 'all', label: 'All case-team memberships' },
  { value: 'first', label: 'First assignment only' },
  { value: 'none', label: 'No case-team alerts' },
];

const validCaseNotificationPreferences = (payload) => (
  payload.success === true
  && CASE_NOTIFICATION_SCOPES.some((scope) => scope.value === payload.case_notification_scope)
);

const SETTINGS_SECTIONS = [
  { id: 'profile', label: 'Profile', icon: CircleUserRound },
  { id: 'appearance', label: 'Appearance & Navigation', icon: Palette },
  { id: 'security', label: 'Password & PIN', icon: ShieldCheck },
  { id: 'shared-links', label: 'Shared Links', icon: Link2 },
  { id: 'integrations', label: 'Integrations', icon: Mail },
  { id: 'preferences', label: 'Preferences', icon: MonitorCog },
];

const APPLICATION_NAV_OPTIONS = [
  {
    value: 'nav',
    title: 'Dock',
    description: 'The floating navigation dock with direct access to primary tools.',
    preview: 'settings-nav-preview dock',
  },
  {
    value: 'compact',
    title: 'Minimized Dock',
    description: 'Keeps the dock collapsed until you choose to expand it.',
    preview: 'settings-nav-preview compact',
  },
  {
    value: 'full',
    title: 'Classic Sidebar',
    description: 'Uses the original left-side application navigation.',
    preview: 'settings-nav-preview sidebar',
  },
];

const apiFetch = (path, options = {}) => fetch(`${API_ROOT}/${path}`, {
  credentials: 'include',
  ...options,
});

const apiJson = async (path, options = {}) => {
  const response = await apiFetch(path, options);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    throw new Error(payload.message || 'The request could not be completed.');
  }
  return payload;
};

const mediaUrl = (path) => {
  const value = String(path || '').trim();
  if (!value || value.startsWith('blob:') || /^https?:\/\//i.test(value)) return value;
  return `${API_ROOT}/${value.replace(/^\/+/, '')}`;
};

const initials = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase() || '')
  .join('') || 'CD';

const Settings = ({
  user,
  setUser,
  theme,
  changeTheme,
  navVariant: navVariantProp,
  switchNavVariant,
}) => {
  const contextNavVariant = useNavVariant();
  const {
    reviewBeforeGeneration,
    saving: savingDocumentPreference,
    setReviewBeforeGeneration,
  } = useDocumentGenerationPreferences();
  const navVariant = navVariantProp || contextNavVariant || 'nav';
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === 'undefined') return 'profile';
    const requested = new URLSearchParams(window.location.search).get('section');
    return SETTINGS_SECTIONS.some((section) => section.id === requested) ? requested : 'profile';
  });
  const [account, setAccount] = useState({
    name: '',
    user: '',
    email: '',
    phone_number: '',
    job_title: '',
    profile_picture: '',
  });
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState('');
  const [removeProfilePicture, setRemoveProfilePicture] = useState(false);
  const [security, setSecurity] = useState({ password: '', confirm_password: '', pin: '' });
  const [sharedLinks, setSharedLinks] = useState([]);
  const [sharedLinksPagination, setSharedLinksPagination] = useState(EMPTY_SHARED_LINKS_PAGINATION);
  const [loadingSharedLinks, setLoadingSharedLinks] = useState(false);
  const [expandedLinkId, setExpandedLinkId] = useState(null);
  const [linkViews, setLinkViews] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [notice, setNotice] = useState(null);
  const [outlookConnection, setOutlookConnection] = useState({ connected: false, status: 'loading' });
  const [outlookMailboxStatus, setOutlookMailboxStatus] = useState(EMPTY_OUTLOOK_MAILBOX_STATUS);
  const [outlookLoading, setOutlookLoading] = useState(true);
  const [outlookWorking, setOutlookWorking] = useState('');
  const [confirmOutlookDisconnect, setConfirmOutlookDisconnect] = useState(false);
  const [hoverPreviews, setHoverPreviews] = useState({
    records: getHoverPreviewPreference(HOVER_PREVIEW_GROUPS.RECORDS),
    documents: getHoverPreviewPreference(HOVER_PREVIEW_GROUPS.DOCUMENTS),
  });
  const [, setPreferenceRevision] = useState(0);
  const [caseNotificationScope, setCaseNotificationScope] = useState(null);
  const [loadingCaseNotificationScope, setLoadingCaseNotificationScope] = useState(false);
  const [savingCaseNotificationScope, setSavingCaseNotificationScope] = useState(false);
  const [caseNotificationScopeError, setCaseNotificationScopeError] = useState('');

  const loadCaseNotificationScope = useCallback(async () => {
    setLoadingCaseNotificationScope(true);
    setCaseNotificationScopeError('');
    try {
      const payload = await apiJson('account-notification-preferences.php');
      if (!validCaseNotificationPreferences(payload)) {
        throw new Error('Notification preferences are unavailable.');
      }
      setCaseNotificationScope(payload.case_notification_scope);
    } catch (error) {
      setCaseNotificationScope(null);
      setCaseNotificationScopeError(error.message || 'Notification preferences are unavailable.');
    } finally {
      setLoadingCaseNotificationScope(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'preferences') loadCaseNotificationScope();
  }, [activeSection, loadCaseNotificationScope]);

  const changeCaseNotificationScope = async (value) => {
    if (savingCaseNotificationScope || loadingCaseNotificationScope
      || !CASE_NOTIFICATION_SCOPES.some((scope) => scope.value === caseNotificationScope)) return;
    if (!CASE_NOTIFICATION_SCOPES.some((scope) => scope.value === value) || value === caseNotificationScope) return;
    setSavingCaseNotificationScope(true);
    setCaseNotificationScopeError('');
    try {
      await ensureCaseDbCsrfToken();
      const payload = await apiJson('account-notification-preferences.php', {
        method: 'POST',
        headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
        body: JSON.stringify({ case_notification_scope: value }),
      });
      if (!validCaseNotificationPreferences(payload)) {
        throw new Error('Notification preferences could not be saved.');
      }
      setCaseNotificationScope(payload.case_notification_scope);
    } catch (error) {
      setCaseNotificationScopeError(error.message || 'Notification preferences could not be saved.');
    } finally {
      setSavingCaseNotificationScope(false);
    }
  };

  const applyOutlookPayload = useCallback((payload = {}) => {
    if (payload.connection) {
      setOutlookConnection(payload.connection);
    }
    if (payload.mailbox_status) {
      setOutlookMailboxStatus((current) => ({ ...current, ...payload.mailbox_status, available: true }));
    }
  }, []);

  const refreshOutlookStatus = useCallback(async ({ showError = false } = {}) => {
    try {
      const payload = await apiJson('jane-outlook.php');
      applyOutlookPayload(payload);
      return payload;
    } catch (error) {
      if (showError) {
        setNotice({ type: 'error', message: error.message || 'Unable to check Outlook status.' });
      }
      return null;
    }
  }, [applyOutlookPayload]);

  useEffect(() => {
    document.title = 'Settings | CaseDB';

    const load = async () => {
      try {
        const [accountPayload, linksPayload, outlookPayload] = await Promise.all([
          apiJson('account.php'),
          apiJson('share.php?action=links').catch(() => ({ data: [] })),
          apiJson('jane-outlook.php').catch(() => ({
            connection: { connected: false, status: 'unavailable', requires_reconnect: false },
          })),
        ]);
        const nextAccount = accountPayload.account || accountPayload;
        setAccount({
          name: nextAccount.name || '',
          user: nextAccount.user || '',
          email: nextAccount.email || '',
          phone_number: nextAccount.phone_number || '',
          job_title: nextAccount.job_title || '',
          profile_picture: nextAccount.profile_picture || '',
        });
        setSharedLinks(Array.isArray(linksPayload.data) ? linksPayload.data : []);
        setSharedLinksPagination({
          ...EMPTY_SHARED_LINKS_PAGINATION,
          ...(linksPayload.pagination || {}),
        });
        setOutlookConnection(outlookPayload.connection || { connected: false, status: 'disconnected' });
        setOutlookMailboxStatus({
          ...EMPTY_OUTLOOK_MAILBOX_STATUS,
          ...(outlookPayload.mailbox_status || {}),
          available: Boolean(outlookPayload.mailbox_status),
        });
        const outlookResult = new URLSearchParams(window.location.search).get('outlook');
        const outlookMessage = new URLSearchParams(window.location.search).get('message');
        if (outlookResult === 'connected') {
          setNotice({ type: 'success', message: 'Outlook connected to Jane.' });
        } else if (outlookResult === 'error') {
          setNotice({ type: 'error', message: outlookMessage || 'Outlook could not be connected.' });
        }
        if (outlookResult || outlookMessage) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('outlook');
          cleanUrl.searchParams.delete('message');
          window.history.replaceState(window.history.state, document.title, `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
        }
      } catch (error) {
        setNotice({ type: 'error', message: error.message || 'Unable to load settings.' });
      } finally {
        setLoading(false);
        setOutlookLoading(false);
      }
    };

    load();
  }, []);

  const outlookMailboxState = String(outlookMailboxStatus?.state || '').toLowerCase();
  const outlookNeedsPolling = ['queued', 'running'].includes(outlookMailboxState);

  useEffect(() => {
    if (!outlookNeedsPolling) return undefined;
    const intervalId = window.setInterval(() => {
      refreshOutlookStatus();
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [outlookNeedsPolling, refreshOutlookStatus]);

  const loadSharedLinksPage = async (page) => {
    const totalPages = Math.max(1, Number(sharedLinksPagination.total_pages) || 1);
    const requestedPage = Math.max(1, Math.min(Number(page) || 1, totalPages));
    if (loadingSharedLinks || requestedPage === Number(sharedLinksPagination.current_page)) return;

    setLoadingSharedLinks(true);
    setNotice(null);
    try {
      const payload = await apiJson(`share.php?action=links&page=${encodeURIComponent(requestedPage)}`);
      setSharedLinks(Array.isArray(payload.data) ? payload.data : []);
      setSharedLinksPagination({
        ...EMPTY_SHARED_LINKS_PAGINATION,
        ...(payload.pagination || {}),
      });
      setExpandedLinkId(null);
      setLinkViews({});
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to load shared links.' });
    } finally {
      setLoadingSharedLinks(false);
    }
  };

  useEffect(() => () => {
    if (profilePreview) URL.revokeObjectURL(profilePreview);
  }, [profilePreview]);

  const displayedProfilePicture = useMemo(() => {
    if (removeProfilePicture) return '';
    if (profilePreview) return profilePreview;
    return mediaUrl(account.profile_picture);
  }, [account.profile_picture, profilePreview, removeProfilePicture]);

  const selectProfileImage = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setNotice({ type: 'error', message: 'Choose an image file.' });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setNotice({ type: 'error', message: 'Profile images must be smaller than 8 MB.' });
      return;
    }
    if (profilePreview) URL.revokeObjectURL(profilePreview);
    setProfileFile(file);
    setProfilePreview(URL.createObjectURL(file));
    setRemoveProfilePicture(false);
  };

  const clearProfileImage = () => {
    if (profilePreview) URL.revokeObjectURL(profilePreview);
    setProfilePreview('');
    setProfileFile(null);
    setRemoveProfilePicture(true);
  };

  const saveProfile = async () => {
    setSaving('profile');
    setNotice(null);
    try {
      const body = new FormData();
      body.append('action', 'profile');
      ['name', 'user', 'email', 'phone_number', 'job_title'].forEach((key) => {
        body.append(key, account[key] || '');
      });
      if (profileFile) body.append('profile_picture', profileFile);
      if (removeProfilePicture) body.append('remove_profile_picture', 'true');

      const response = await apiFetch('account.php', { method: 'POST', body });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || 'Unable to save profile.');
      }

      const savedAccount = payload.account || payload;
      setAccount((current) => ({ ...current, ...savedAccount }));
      setProfileFile(null);
      setProfilePreview('');
      setRemoveProfilePicture(false);
      setUser?.((current) => current ? {
        ...current,
        name: savedAccount.name,
        user: savedAccount.user,
        email: savedAccount.email,
        profile_picture: savedAccount.profile_picture,
      } : current);
      setNotice({ type: 'success', message: 'Profile saved.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to save profile.' });
    } finally {
      setSaving('');
    }
  };

  const saveSecurity = async () => {
    if (!security.password && !security.pin) {
      setNotice({ type: 'error', message: 'Enter a new password or PIN.' });
      return;
    }
    if (security.password !== security.confirm_password) {
      setNotice({ type: 'error', message: 'Passwords do not match.' });
      return;
    }

    setSaving('security');
    setNotice(null);
    try {
      await apiJson('account.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'security', password: security.password, pin: security.pin }),
      });
      setSecurity({ password: '', confirm_password: '', pin: '' });
      setNotice({ type: 'success', message: 'Security settings saved.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to save security settings.' });
    } finally {
      setSaving('');
    }
  };

  const chooseApplicationNav = (variant) => {
    switchNavVariant?.(variant);
    setPreferenceRevision((revision) => revision + 1);
  };

  const changeHoverPreview = (group, nextEnabled) => {
    setHoverPreviews((current) => {
      const key = group === HOVER_PREVIEW_GROUPS.DOCUMENTS ? 'documents' : 'records';
      const enabled = setHoverPreviewPreference(group, nextEnabled);
      return { ...current, [key]: enabled };
    });
  };

  const changeDocumentReview = async (nextEnabled) => {
    setNotice(null);
    try {
      const savedEnabled = await setReviewBeforeGeneration(nextEnabled);
      setNotice({
        type: 'success',
        message: savedEnabled
          ? 'Document data review is on.'
          : 'Document data review is off.',
      });
    } catch (error) {
      setNotice({
        type: 'error',
        message: error.message || 'Unable to save the document review setting.',
      });
    }
  };

  const connectOutlook = async ({ acknowledgeAiProcessing = false } = {}) => {
    setOutlookWorking('connect');
    setNotice(null);
    try {
      const payload = await apiJson('jane-outlook.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'start',
          return_origin: window.location.origin,
          acknowledge_ai_processing: acknowledgeAiProcessing === true,
        }),
      });
      if (!payload.authorization_url) throw new Error('Microsoft authorization is unavailable.');
      window.location.assign(payload.authorization_url);
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to connect Outlook.' });
      setOutlookWorking('');
    }
  };

  const disconnectOutlook = async () => {
    setOutlookWorking('disconnect');
    setNotice(null);
    try {
      const payload = await apiJson('jane-outlook.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'disconnect' }),
      });
      applyOutlookPayload(payload);
      if (!payload.connection) {
        setOutlookConnection({ connected: false, status: 'revoked', requires_reconnect: false });
      }
      if (!payload.mailbox_status) {
        setOutlookMailboxStatus(EMPTY_OUTLOOK_MAILBOX_STATUS);
      }
      setConfirmOutlookDisconnect(false);
      setNotice({ type: 'success', message: 'Outlook disconnected from Jane.' });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to disconnect Outlook.' });
    } finally {
      setOutlookWorking('');
    }
  };

  const checkOutlookNow = async () => {
    setOutlookWorking('check');
    setNotice(null);
    try {
      const payload = await apiJson('jane-outlook.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check_now' }),
      });
      applyOutlookPayload(payload);
      if (!payload.mailbox_status) {
        setOutlookMailboxStatus((current) => ({
          ...current,
          state: payload.job?.status || 'queued',
          active_job_id: payload.job?.id || current.active_job_id || null,
        }));
      }
      setNotice({
        type: 'success',
        message: payload.mailbox_status
          ? 'Outlook connection confirmed.'
          : (payload.job?.already_active ? 'Outlook is already being checked.' : 'Jane is checking Outlook now.'),
      });
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to check Outlook.' });
    } finally {
      setOutlookWorking('');
    }
  };

  const toggleLinkViews = async (linkId) => {
    if (expandedLinkId === linkId) {
      setExpandedLinkId(null);
      return;
    }
    setExpandedLinkId(linkId);
    if (linkViews[linkId]) return;

    try {
      const payload = await apiJson(`share.php?action=views&share_link_id=${encodeURIComponent(linkId)}`);
      setLinkViews((current) => ({
        ...current,
        [linkId]: Array.isArray(payload.data) ? payload.data : [],
      }));
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Unable to load link activity.' });
    }
  };

  const resetPreference = (name) => {
    if (name === 'theme') {
      if (theme === 'dark') changeTheme?.();
      eraseCookie('theme');
    } else if (name === 'application_nav') {
      eraseCookie('nav_variant_v2');
      chooseApplicationNav('nav');
    } else if (name === 'case_vitals') {
      eraseCookie('show_vitals_in_header');
    } else if (name === 'record_previews') {
      resetHoverPreviewPreference(HOVER_PREVIEW_GROUPS.RECORDS);
      setHoverPreviews((current) => ({ ...current, records: false }));
    } else if (name === 'document_previews') {
      resetHoverPreviewPreference(HOVER_PREVIEW_GROUPS.DOCUMENTS);
      setHoverPreviews((current) => ({ ...current, documents: false }));
    } else if (name === 'document_generation_review') {
      changeDocumentReview(false);
    }
    setPreferenceRevision((revision) => revision + 1);
  };

  const preferences = [
    {
      id: 'theme',
      name: 'Color theme',
      description: 'Remembers whether CaseDB uses light or dark mode on this browser.',
      value: theme === 'dark' ? 'Dark' : 'Light',
    },
    {
      id: 'application_nav',
      name: 'Application navigation',
      description: 'Remembers the navigation layout selected above.',
      value: APPLICATION_NAV_OPTIONS.find((option) => option.value === navVariant)?.title || 'Dock',
    },
    {
      id: 'case_vitals',
      name: 'Case header vitals',
      description: 'Remembers whether the vitals row is expanded in a case.',
      value: getCookie('show_vitals_in_header') === '0' ? 'Collapsed' : 'Expanded',
    },
    {
      id: 'document_generation_review',
      name: 'Review document data before generation',
      description: 'Shows the CaseDB values used by ReportFusion and DocGen before creating the file. This preference follows your account.',
      value: reviewBeforeGeneration ? 'On' : 'Off',
      enabled: reviewBeforeGeneration,
      toggle: true,
      saving: savingDocumentPreference,
      onToggle: changeDocumentReview,
    },
    {
      id: 'record_previews',
      name: 'Case Previews',
      description: 'Shows a quick case summary when you pause over a case.',
      value: hoverPreviews.records ? 'On' : 'Off',
      previewGroup: HOVER_PREVIEW_GROUPS.RECORDS,
      enabled: hoverPreviews.records,
      toggle: true,
      onToggle: (enabled) => changeHoverPreview(HOVER_PREVIEW_GROUPS.RECORDS, enabled),
    },
    {
      id: 'document_previews',
      name: 'Document Previews',
      description: 'Shows document previews and folder contents when you pause over them.',
      value: hoverPreviews.documents ? 'On' : 'Off',
      previewGroup: HOVER_PREVIEW_GROUPS.DOCUMENTS,
      enabled: hoverPreviews.documents,
      toggle: true,
      onToggle: (enabled) => changeHoverPreview(HOVER_PREVIEW_GROUPS.DOCUMENTS, enabled),
    },
  ];

  const formatSharedLocation = (link) => {
    const caseName = String(link.case_name || '').trim();
    const segments = String(link.target_key || '')
      .replace(/\\/g, '/')
      .replace(/^cases\/\d+\//i, '')
      .replace(/\{\{Name\}\}/gi, caseName)
      .split('/')
      .map((segment) => segment.trim())
      .filter(Boolean);

    if (caseName && segments[0]?.toLowerCase() === caseName.toLowerCase()) {
      segments.shift();
    }

    return segments.join(' / ') || 'Case documents';
  };

  const renderProfile = () => (
    <section className="settings-panel" aria-labelledby="settings-profile-title">
      <div className="settings-panel-header">
        <div>
          <h3 id="settings-profile-title">Profile</h3>
          <span className="subtext">The identity shown to your firm on assignments, activity, and documents.</span>
        </div>
      </div>

      <div className="settings-profile-layout">
        <div className="settings-avatar-editor">
          <div className="settings-avatar">
            {displayedProfilePicture
              ? <img src={displayedProfilePicture} alt="Profile" />
              : <span>{initials(account.name || user?.name)}</span>}
          </div>
          <div className="settings-avatar-actions">
            <label className="action alt" htmlFor="settings-profile-image">
              <Camera size={16} /> Choose Image
            </label>
            <input
              id="settings-profile-image"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={selectProfileImage}
            />
            {(displayedProfilePicture || account.profile_picture) && (
              <button type="button" className="action tertiary" onClick={clearProfileImage}>
                <Trash2 size={16} /> Remove
              </button>
            )}
          </div>
          <span className="small-text settings-muted">JPG, PNG, WebP, or GIF. Maximum 8 MB.</span>
        </div>

        <div className="settings-fields-grid">
          <label className="settings-field">
            <span>Name</span>
            <input value={account.name} placeholder="Enter your name" onChange={(event) => setAccount({ ...account, name: event.target.value })} />
          </label>
          <label className="settings-field">
            <span>Username</span>
            <input value={account.user} placeholder="Enter your username" onChange={(event) => setAccount({ ...account, user: event.target.value })} autoComplete="username" />
          </label>
          <label className="settings-field">
            <span>Email</span>
            <input type="email" value={account.email} placeholder="Enter your email" onChange={(event) => setAccount({ ...account, email: event.target.value })} autoComplete="email" />
          </label>
          <label className="settings-field">
            <span>Phone</span>
            <input type="tel" value={account.phone_number} placeholder="Enter your phone number" onChange={(event) => setAccount({ ...account, phone_number: event.target.value })} autoComplete="tel" />
          </label>
          <label className="settings-field settings-field-wide">
            <span>Job title</span>
            <input value={account.job_title} placeholder="Enter your job title" onChange={(event) => setAccount({ ...account, job_title: event.target.value })} autoComplete="organization-title" />
          </label>
        </div>
      </div>

      <div className="settings-panel-actions">
        <button type="button" className="action primary" onClick={saveProfile} disabled={saving === 'profile' || loading}>
          {saving === 'profile' ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </section>
  );

  const renderAppearance = () => (
    <div className="settings-panel-stack">
      <section className="settings-panel">
        <div className="settings-panel-header settings-panel-header-compact">
          <div>
            <h3>Theme</h3>
            <span className="subtext">Choose the color mode used throughout CaseDB.</span>
          </div>
          <button type="button" className="action alt" onClick={changeTheme}>
            <Palette size={16} /> Use {theme === 'dark' ? 'Light' : 'Dark'} Mode
          </button>
        </div>
      </section>

      <section className="settings-panel">
        <div className="settings-panel-header">
          <div>
            <h3>Application Navigation</h3>
            <span className="subtext">Controls how you move between Cases, Leads, Library, Reports, and other primary pages.</span>
          </div>
        </div>
        <div className="settings-choice-grid three-column">
          {APPLICATION_NAV_OPTIONS.map((option) => (
            <button
              type="button"
              className={`settings-choice${navVariant === option.value ? ' active' : ''}`}
              key={option.value}
              onClick={() => chooseApplicationNav(option.value)}
            >
              <span className={option.preview} aria-hidden="true"><i /><i /><i /></span>
              <strong>{option.title}</strong>
              <span className="small-text settings-muted">{option.description}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );

  const renderSecurity = () => (
    <section className="settings-panel">
      <div className="settings-panel-header">
        <div>
          <h3>Password & PIN</h3>
          <span className="subtext">Your password signs you in. Your PIN confirms protected actions.</span>
        </div>
      </div>
      <div className="settings-fields-grid security">
        <label className="settings-field">
          <span>New password</span>
          <input type="password" value={security.password} onChange={(event) => setSecurity({ ...security, password: event.target.value })} autoComplete="new-password" placeholder="At least 10 characters" />
        </label>
        <label className="settings-field">
          <span>Confirm password</span>
          <input type="password" value={security.confirm_password} onChange={(event) => setSecurity({ ...security, confirm_password: event.target.value })} autoComplete="new-password" />
        </label>
        <label className="settings-field">
          <span>New 4-digit PIN</span>
          <input type="password" inputMode="numeric" maxLength={4} value={security.pin} onChange={(event) => setSecurity({ ...security, pin: event.target.value.replace(/\D/g, '').slice(0, 4) })} autoComplete="off" />
        </label>
      </div>
      <div className="settings-security-note">
        <KeyRound size={17} />
        <span>Leaving either option blank keeps its current value.</span>
      </div>
      <div className="settings-panel-actions">
        <button type="button" className="action primary" onClick={saveSecurity} disabled={saving === 'security'}>
          {saving === 'security' ? 'Saving...' : 'Save Security Settings'}
        </button>
      </div>
    </section>
  );

  const renderSharedLinks = () => {
    const currentPage = Math.max(1, Number(sharedLinksPagination.current_page) || 1);
    const totalPages = Math.max(1, Number(sharedLinksPagination.total_pages) || 1);
    const perPage = Math.max(1, Number(sharedLinksPagination.per_page) || 100);
    const totalLinks = Math.max(0, Number(sharedLinksPagination.total) || 0);
    const firstVisibleLink = totalLinks === 0 ? 0 : ((currentPage - 1) * perPage) + 1;
    const lastVisibleLink = Math.min(totalLinks, firstVisibleLink + sharedLinks.length - 1);

    return (
      <section className="settings-panel settings-links-panel">
      <div className="settings-panel-header">
        <div>
          <h3>Shared Links</h3>
          <span className="subtext">Active and expired links you created. Open a row to review its activity.</span>
        </div>
        <span className="settings-count">{totalLinks}</span>
      </div>
      <div className="settings-table-wrap">
        <table className="settings-table">
          <colgroup>
            <col className="settings-col-case" />
            <col className="settings-col-item" />
            <col className="settings-col-views" />
            <col className="settings-col-expires" />
            <col className="settings-col-link" />
          </colgroup>
          <thead>
            <tr><th>Case</th><th>Shared item</th><th>Views</th><th>Expires</th><th>Link</th></tr>
          </thead>
          <tbody>
            {sharedLinks.length === 0 && (
              <tr className="settings-table-empty">
                <td colSpan={5}>{loadingSharedLinks ? 'Loading shared links…' : 'You have not created any shared links.'}</td>
              </tr>
            )}
            {sharedLinks.map((link) => {
              const expired = Number(link.expired) === 1 || link.expired === true;
              return (
                <React.Fragment key={link.id}>
                <tr
                  className={`clickable-row${expandedLinkId === link.id ? ' expanded' : ''}${expired ? ' expired' : ''}`}
                  onClick={() => toggleLinkViews(link.id)}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleLinkViews(link.id);
                    }
                  }}
                >
                  <td className="settings-link-case"><strong>{link.case_name || 'No case'}</strong></td>
                  <td className="settings-link-location" title={formatSharedLocation(link)}>{formatSharedLocation(link)}</td>
                  <td className="settings-link-views">{link.views_count || 0}</td>
                  <td className="settings-link-expiration">
                    {link.expires_at
                      ? `${expired ? 'Expired' : 'Expires'} ${new Date(link.expires_at).toLocaleDateString()}`
                      : 'No expiration'}
                  </td>
                  <td className="settings-link-open">
                    <a href={`/share/${link.token}`} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                      {expired ? 'Review' : 'Open'} <ExternalLink size={14} />
                    </a>
                  </td>
                </tr>
                {expandedLinkId === link.id && (
                  <tr className="settings-link-activity-row">
                    <td colSpan={5}>
                      {(linkViews[link.id] || []).length === 0 ? (
                        <div className="settings-link-empty subtext">No views recorded.</div>
                      ) : (
                        <div className="settings-link-activity">
                          <div className="settings-link-activity-header">
                            <span>User</span><span>Viewed At</span><span>IP Address</span>
                          </div>
                          {(linkViews[link.id] || []).map((view, index) => (
                            <div key={view.id || index}>
                              <span>{view.viewer_name || 'External viewer'}</span>
                              <span>{new Date(view.created_at).toLocaleString()}</span>
                              <span>{view.ip_address || 'IP unavailable'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalLinks > 0 && (
        <div className="settings-pagination" aria-label="Shared links pages">
          <button
            type="button"
            className="action tertiary"
            onClick={() => loadSharedLinksPage(currentPage - 1)}
            disabled={loadingSharedLinks || !sharedLinksPagination.has_prev}
          >
            Previous
          </button>
          <span className="settings-pagination-status">
            <strong>Page {currentPage} of {totalPages}</strong>
            <span className="subtext">Showing {firstVisibleLink}–{lastVisibleLink} of {totalLinks}</span>
          </span>
          <button
            type="button"
            className="action tertiary"
            onClick={() => loadSharedLinksPage(currentPage + 1)}
            disabled={loadingSharedLinks || !sharedLinksPagination.has_next}
          >
            Next
          </button>
        </div>
      )}
      </section>
    );
  };

  const renderPreferences = () => (
    <section className="settings-panel">
      <div className="settings-panel-header">
        <div>
          <h3>Preferences</h3>
          <span className="subtext">Control your CaseDB experience. Notification and document review preferences follow your account; display preferences are remembered in this browser.</span>
        </div>
      </div>
      <div className="settings-preference-list">
        {caseNotificationScopeError && <div className="subtext" role="alert">{caseNotificationScopeError}</div>}
        <div className="settings-preference-row settings-notification-scope-row form-group nm">
          <div>
            <label htmlFor="case-notification-scope">Case-team communication alerts</label>
            <span className="subtext" id="case-notification-scope-help">This one setting controls both case-email and SMS alerts. First assignment means you are the first person listed in at least one case-team role. New preferences default to first assignment only. Direct mentions and task notifications are unchanged.</span>
          </div>
          <span className="settings-preference-value" role="status">
            {loadingCaseNotificationScope ? 'Loading…' : savingCaseNotificationScope ? 'Saving…' : ''}
          </span>
          <div className="select-wrapper">
            <select
              id="case-notification-scope"
              className="filter-select"
              value={caseNotificationScope ?? ''}
              onChange={(event) => changeCaseNotificationScope(event.target.value)}
              disabled={loadingCaseNotificationScope || savingCaseNotificationScope || caseNotificationScope === null}
              aria-describedby="case-notification-scope-help"
            >
              {caseNotificationScope === null && <option value="" disabled>{loadingCaseNotificationScope ? 'Loading…' : 'Unavailable'}</option>}
              {CASE_NOTIFICATION_SCOPES.map((scope) => <option key={scope.value} value={scope.value}>{scope.label}</option>)}
            </select>
            <ChevronDown size={16} className="select-chevron" aria-hidden="true" />
          </div>
          {caseNotificationScope === null && !loadingCaseNotificationScope && <button type="button" className="action tertiary" onClick={loadCaseNotificationScope}>Retry</button>}
        </div>
        {preferences.map((preference) => (
          <div className="settings-preference-row" key={preference.id}>
            <div>
              <strong>{preference.name}</strong>
              <span className="subtext">{preference.description}</span>
            </div>
            <span className="settings-preference-value">
              {preference.toggle ? (preference.saving ? 'Saving…' : '') : preference.value}
            </span>
            {preference.toggle ? (
              <Toggle
                value={preference.enabled}
                onChange={(enabled) => {
                  if (!preference.saving) preference.onToggle?.(enabled);
                }}
                labels={['Off', 'On']}
              />
            ) : (
              <button type="button" className="action tertiary" onClick={() => resetPreference(preference.id)}>Reset</button>
            )}
          </div>
        ))}
      </div>
      <div className="settings-cookie-explanation">
        <ShieldCheck size={18} />
        <div>
          <strong>Sign-in cookies are managed automatically.</strong>
          <span className="subtext">They are secured separately and are removed when you sign out or when your session expires.</span>
        </div>
      </div>
    </section>
  );

  const renderIntegrations = () => (
    <OutlookIntegrationPanel
      connection={outlookConnection}
      mailboxStatus={outlookMailboxStatus}
      loading={outlookLoading}
      working={outlookWorking}
      confirmingDisconnect={confirmOutlookDisconnect}
      onConnect={connectOutlook}
      onRequestDisconnect={() => setConfirmOutlookDisconnect(true)}
      onCancelDisconnect={() => setConfirmOutlookDisconnect(false)}
      onConfirmDisconnect={disconnectOutlook}
      onCheckNow={checkOutlookNow}
    />
  );

  const renderActiveSection = () => {
    if (loading) return <section className="settings-panel"><span className="subtext">Loading settings...</span></section>;
    if (activeSection === 'appearance') return renderAppearance();
    if (activeSection === 'security') return renderSecurity();
    if (activeSection === 'shared-links') return renderSharedLinks();
    if (activeSection === 'integrations') return renderIntegrations();
    if (activeSection === 'preferences') return renderPreferences();
    return renderProfile();
  };

  return (
    <div id="user-settings" className={`page-container settings-page ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
      <div id="page-header" className="settings-page-header">
        <h2>Settings</h2>
        <span className="subtext">Manage your profile, security, and personal CaseDB experience.</span>
      </div>

      <div className="settings-layout">
        <aside className="settings-nav" role="navigation" aria-label="Settings sections">
          {SETTINGS_SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <button
                type="button"
                className={activeSection === section.id ? 'active' : ''}
                key={section.id}
                onClick={() => setActiveSection(section.id)}
              >
                <Icon size={17} />
                <span>{section.label}</span>
              </button>
            );
          })}
        </aside>
        <main className="settings-content">{renderActiveSection()}</main>
      </div>

      {notice && <Alert message={notice.message} type={notice.type} onClose={() => setNotice(null)} />}
    </div>
  );
};

export default Settings;
